import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Pencil, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/orgApi";
import { USR } from "@/constants/testIds";
import UserForm from "@/components/user/UserForm";
import "@/styles/emr-catalog.css";

const ROLE_LABELS = {
  administrator: "Administrator",
  md_do: "Physician / Provider",
  front_desk: "Front Desk / Secretary",
  biller: "Biller",
  medical_assistant: "MA",
  nurse: "Nurse",
  np: "NP",
  pa: "PA",
};
const roleLabel = (r) => ROLE_LABELS[r] || r;
const ROLE_ORDER = { administrator: 0, md_do: 1, front_desk: 2, biller: 3 };

function orgOf(u) {
  return u.primaryOrganization?.display || u.organizations?.[0]?.display || "—";
}

function locationOf(u) {
  const access = u.locationAccess || [];
  const def = access.find((la) => la.isDefault) || access[0];
  if (def?.location?.display) return def.location.display;
  if (u.orgWide) return "Org-wide";
  return "—";
}

function primaryRole(u) {
  return (u.roles || [])[0] || "";
}

export default function UsersPage() {
  const [items, setItems] = useState([]);
  const [demoIds, setDemoIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get("/users", { params: q ? { q } : {} });
      setItems(data.items || []);
      setDemoIds(new Set(data.demoIds || []));
    } catch { toast.error("Failed to load users."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setTimeout(() => load(search.trim() || undefined), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success("User deactivated.");
      setDeleteTarget(null); load(search.trim() || undefined);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete."); }
  };

  return (
    <div data-testid={USR.page} className="emr-catalog emr-catalog--full-table">
      <div className="emr-catalog__pagehead">
        <div>
          <h1 data-testid={USR.title} className="emr-catalog__title">Users</h1>
          <p className="emr-catalog__subtitle">Application accounts &amp; access — not FHIR Practitioner</p>
        </div>
        <Button
          data-testid={USR.newBtn}
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="btn emr-catalog__btn"
        >
          <Plus className="emr-catalog__btn-icon" /> New User
        </Button>
      </div>

      <div className="emr-catalog__workspace">
        <div className="emr-catalog__side">
          <section className="card emr-catalog__section">
            <h2 className="emr-catalog__section-title">Purpose</h2>
            <p className="emr-catalog__copy">
              Create and maintain application accounts, roles, and location access for this organization.
            </p>
          </section>
          <section className="card emr-catalog__section">
            <h2 className="emr-catalog__section-title">Search</h2>
            <div className="emr-catalog__filters emr-catalog__filters--search-only">
              <div className="emr-catalog__search">
                <Search className="emr-catalog__search-icon" aria-hidden="true" />
                <input
                  data-testid={USR.search}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name / email…"
                  className="emr-catalog__input"
                />
              </div>
            </div>
          </section>
        </div>

        <section className="card emr-catalog__section emr-catalog__catalog">
          <h2 className="emr-catalog__section-title">Users</h2>
          {loading ? (
            <div data-testid={USR.loading} className="emr-catalog__status-msg">Loading…</div>
          ) : items.length === 0 ? (
            <div data-testid={USR.emptyState} className="emr-catalog__empty">
              <Users className="emr-catalog__empty-icon" aria-hidden="true" />
              <p>No users yet.</p>
            </div>
          ) : (
            <div className="emr-catalog__table-wrap">
              <table data-testid={USR.table} className="striped highlight emr-catalog__table emr-catalog__table--wide">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Email</th>
                    <th>Organization</th>
                    <th>Location</th>
                    <th>Status</th>
                    <th className="emr-catalog__actions-col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...items].sort((a, b) => {
                    const ra = ROLE_ORDER[primaryRole(a)] ?? 99;
                    const rb = ROLE_ORDER[primaryRole(b)] ?? 99;
                    if (ra !== rb) return ra - rb;
                    return (a.displayName || "").localeCompare(b.displayName || "");
                  }).map((u) => (
                    <tr key={u.id} data-testid={`${USR.row}-${u.id}`}>
                      <td className="emr-catalog__name">
                        {u.displayName || `${u.firstName} ${u.lastName}`}
                        {demoIds.has(u.id) && (
                          <span data-testid={`demo-badge-${u.id}`} className="emr-catalog__demo">DEMO</span>
                        )}
                      </td>
                      <td>
                        <div className="emr-catalog__chips">
                          {(u.roles || []).map((r) => (
                            <span key={r} className="emr-catalog__token">{roleLabel(r)}</span>
                          ))}
                        </div>
                      </td>
                      <td className="emr-catalog__muted">{u.email}</td>
                      <td className="emr-catalog__muted">{orgOf(u)}</td>
                      <td className="emr-catalog__muted">{locationOf(u)}</td>
                      <td>
                        {u.active !== false
                          ? <span className="emr-catalog__status emr-catalog__status--active">Active</span>
                          : <span className="emr-catalog__status emr-catalog__status--inactive">Inactive</span>}
                      </td>
                      <td>
                        <div className="emr-catalog__row-actions">
                          <Button variant="ghost" size="icon" title="Edit" className="emr-catalog-icon-btn"
                            data-testid={`${USR.editBtn}-${u.id}`}
                            onClick={() => { setEditing(u); setFormOpen(true); }}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" title="Deactivate" className="emr-catalog-icon-btn emr-catalog-icon-btn--danger"
                            data-testid={`${USR.deleteBtn}-${u.id}`}
                            onClick={() => setDeleteTarget(u)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <UserForm open={formOpen} editing={editing} onClose={() => setFormOpen(false)}
        onSaved={() => load(search.trim() || undefined)} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid={USR.deleteDialog}>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate user?</AlertDialogTitle>
            <AlertDialogDescription>
              This deactivates the account. The employee record, roles, and clinical history are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={USR.confirmDelete} onClick={confirmDelete}
              className="emr-catalog__btn emr-catalog__btn--danger">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
